<template>
  <div class="addon-activation-view">
    <div class="container">
      <AppHeader tagline="Add-on activation payment portal." />

      <!-- Loading State -->
      <LoadingSpinner
        v-if="loading"
        :text="loadingText"
        size="lg"
      />

      <!-- Error State -->
      <StatusCard
        v-else-if="errorMessage"
        variant="error"
        title="Unable to Load"
        :message="errorMessage"
        show-help
      />

      <!-- Main Content -->
      <template v-else>
        <!-- Payment Completed -->
        <StatusCard
          v-if="isProcessed"
          variant="success"
          size="lg"
          title="Payment Completed"
          message="Your add-on has been activated successfully."
          submessage="Thank you for your payment."
        >
          <div v-if="invoiceNumber" class="invoice-badge">
            <span class="badge-label">Invoice</span>
            <span class="badge-value">{{ invoiceNumber }}</span>
          </div>
        </StatusCard>

        <!-- No Payment Required -->
        <StatusCard
          v-else-if="!paymentRequired"
          variant="info"
          title="No Payment Required"
          message="Your request has been approved and no payment is required."
          submessage="We will proceed with activation and notify you."
        />

        <!-- Payment Not Ready -->
        <StatusCard
          v-else-if="!isAccepted"
          variant="warning"
          title="Payment Not Ready"
          message="This request has not been approved for payment yet."
          submessage="Please contact support if you have questions."
        />

        <!-- Payment Flow -->
        <template v-else>
          <!-- Section Title -->
          <div class="section-header">
            <h2 class="section-title">Add-on Activation Payment</h2>
          </div>

          <div class="payment-layout">
            <!-- Request Details Card -->
            <BaseCard title="Request Details">
              <DetailList :items="detailItems" :dividers="true" class="mb-4" />

              <div class="price-breakdown">
                <div class="price-row">
                  <span>Setup Fee</span>
                  <span>{{ $utils.formatMoney(request.addonSetupFee || 0) }}</span>
                </div>
                <div class="price-row">
                  <span>Subscription (prorated)</span>
                  <span>{{ $utils.formatMoney(request.proratedSubscriptionAmount || 0) }}</span>
                </div>
                <div class="price-row price-row--total">
                  <span>Total Due</span>
                  <span class="total-amount">{{ $utils.formatMoney(request.totalDue || 0) }}</span>
                </div>
              </div>
            </BaseCard>

            <!-- Payment Method Card -->
            <BaseCard title="Payment Method">
              <!-- Method Selection -->
              <div v-if="selectedPaymentMethod === null" class="method-selection">
                <p class="selection-prompt">Choose how you would like to pay.</p>

                <button
                  type="button"
                  class="method-btn"
                  :disabled="paymentProcessing"
                  @click="selectPaymentMethod('EFT')"
                >
                  <div class="method-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                      <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                  </div>
                  <div class="method-info">
                    <span class="method-name">Bank / EFT</span>
                    <span class="method-desc">Pay via bank transfer</span>
                  </div>
                  <svg class="method-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>

                <button
                  type="button"
                  class="method-btn"
                  :disabled="paymentProcessing"
                  @click="selectPaymentMethod('Kazang')"
                >
                  <div class="method-icon method-icon--kazang">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                      <line x1="12" y1="18" x2="12.01" y2="18"></line>
                    </svg>
                  </div>
                  <div class="method-info">
                    <span class="method-name">Mobile Money</span>
                    <span class="method-desc">Pay via Kazang</span>
                  </div>
                  <svg class="method-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>

              <!-- Selected Method -->
              <div v-else class="selected-method">
                <div class="selected-header">
                  <span class="selected-label">Method: {{ selectedPaymentMethod }}</span>
                  <BaseButton
                    variant="outline"
                    size="sm"
                    :disabled="paymentProcessing"
                    @click="selectedPaymentMethod = null"
                  >
                    Change
                  </BaseButton>
                </div>

                <!-- Kazang Payment -->
                <div v-if="selectedPaymentMethod === 'Kazang'" class="kazang-section">
                  <p class="payment-prompt">Pay securely via Kazang mobile money.</p>
                  <BaseButton
                    variant="secondary"
                    full-width
                    :loading="paymentProcessing"
                    @click="startKazangPayment"
                  >
                    Pay {{ $utils.formatMoney(request.totalDue || 0) }}
                  </BaseButton>
                </div>

                <!-- EFT Payment -->
                <div v-else-if="selectedPaymentMethod === 'EFT'" class="eft-section">
                  <!-- Payment Reference -->
                  <InfoBanner
                    label="Payment Reference"
                    :value="suggestedReference"
                    copyable
                    :copy-value="suggestedReference"
                    class="mb-4"
                  />

                  <!-- EFT Reference Input -->
                  <BaseInput
                    v-model="eftReference"
                    label="EFT Reference Used"
                    placeholder="Enter your EFT reference"
                    class="mb-4"
                  />

                  <BaseButton
                    full-width
                    :loading="paymentProcessing"
                    @click="submitEftReference"
                  >
                    Submit EFT Reference
                  </BaseButton>

                  <!-- Success Message -->
                  <div v-if="eftSubmitted" class="success-notice">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <span>Reference submitted. We will confirm and activate your add-on.</span>
                  </div>

                  <!-- Bank Details -->
                  <BankDetails bordered :show-notice="false" />
                </div>
              </div>
            </BaseCard>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { responses } from "@/global-variables/responses";
import AppHeader from "@/components/layout/AppHeader.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import BankDetails from "@/components/common/BankDetails.vue";
import { StatusCard, BaseCard, BaseButton, BaseInput, InfoBanner, DetailList } from "@/components/ui";

const getSubscriptionApi = () => window.subscriptionApi || window.API || "";

export default {
  name: "AddonActivationPayment",

  components: {
    AppHeader,
    LoadingSpinner,
    BankDetails,
    StatusCard,
    BaseCard,
    BaseButton,
    BaseInput,
    InfoBanner,
    DetailList
  },

  data() {
    return {
      requestId: null,
      request: null,
      loading: true,
      loadingText: "Loading request details...",
      errorMessage: "",
      selectedPaymentMethod: null,
      paymentProcessing: false,
      eftReference: "",
      eftSubmitted: false,
      invoiceNumber: null
    };
  },

  computed: {
    isProcessed() {
      if (!this.request || !this.request.status) return false;
      return this.request.status.toUpperCase() === "PROCESSED";
    },
    isAccepted() {
      if (!this.request || !this.request.status) return false;
      const status = this.request.status.toUpperCase();
      return status === "ACCEPTED" || status === "PROCESSED";
    },
    paymentRequired() {
      if (!this.request) return false;
      return this.request.paymentRequired !== false;
    },
    suggestedReference() {
      return this.requestId ? `ADDON-${this.requestId}` : "";
    },
    detailItems() {
      if (!this.request) return [];
      const items = [
        {
          label: 'Store',
          value: this.request.branchName
            ? `${this.request.storeName} - ${this.request.branchName}`
            : this.request.storeName
        },
        { label: 'Add-on', value: this.request.addonName },
        { label: 'Setup Type', value: this.request.setupType }
      ];
      if (this.request.nextSubscriptionDate) {
        items.push({ label: 'Next Billing Date', value: this.request.nextSubscriptionDate });
      }
      return items;
    }
  },

  methods: {
    selectPaymentMethod(method) {
      this.selectedPaymentMethod = method;
    },
    fetchRequestDetails() {
      return axios.get(getSubscriptionApi() + `addons/activation-requests/${this.requestId}`)
        .then(({ data }) => {
          this.request = data;
          if (!this.eftReference) {
            this.eftReference = this.suggestedReference;
          }
        })
        .catch((err) => {
          this.errorMessage = "Unable to load request details.";
          return errorHandler.tomcatError(err);
        });
    },
    recordAddonPayment(paymentType, paymentReference) {
      const payload = {
        storeId: this.request.storeId,
        addonId: this.request.addonId,
        setupOptionType: this.request.setupType,
        paymentType: paymentType,
        activationRequestId: this.requestId,
        paymentReference: paymentReference
      };
      return axios.post(getSubscriptionApi() + "addons/payments", payload);
    },
    processAddonPayment() {
      const payload = {
        addonId: this.request.addonId,
        storeId: this.request.storeId,
        processedBy: "CLIENT_PORTAL",
        setupOptionType: this.request.setupType,
        activationRequestId: this.requestId
      };
      return axios.post(getSubscriptionApi() + "addons/payments/process", payload);
    },
    startKazangPayment() {
      if (!this.request) return;
      this.paymentProcessing = true;
      this.selectedPaymentMethod = "Kazang";
      const successUrl = `${window.location.origin}/addon-activation`;
      const obj = {
        API_KEY: "945ac88d-e88a-11ed-91dd-00163e8d4996",
        amount: this.request.totalDue,
        Service_Description: `Addon activation - ${this.request.addonName}`,
        Client_Refrence: this.requestId,
        Cancel_Url: "https://gatewaykazang.chrilantech.com/test-failed",
        Sucess_Url: successUrl
      };
      return axios.post("https://lna.pos.co.zm/KazangPaymentGateway/GateWay/CreateTransaction", obj)
        .then(({ data }) => {
          if (data.Error) throw new Error(data.Error);
          return window.open(`https://gatewaykazang.chrilantech.com/kazangOnline?id=${data.ID}`, "_self");
        })
        .catch((err) => errorHandler.tomcatError(err))
        .finally(() => {
          this.paymentProcessing = false;
        });
    },
    submitEftReference() {
      if (!this.eftReference || !this.eftReference.trim()) {
        return responses.throwErr("Please enter your EFT reference.", "Missing reference");
      }
      this.paymentProcessing = true;
      return this.recordAddonPayment("EFT", this.eftReference.trim())
        .then(() => {
          this.eftSubmitted = true;
          responses.showSuccess("EFT reference submitted");
        })
        .catch((err) => errorHandler.tomcatError(err))
        .finally(() => {
          this.paymentProcessing = false;
        });
    },
    finalizeKazangPayment() {
      const kazangRef = this.$route.query.KazangRef;
      if (!kazangRef) return Promise.resolve();
      if (this.isProcessed) return Promise.resolve();
      this.paymentProcessing = true;
      this.loadingText = "Processing payment...";
      this.selectedPaymentMethod = "Kazang";
      return this.recordAddonPayment("Kazang", kazangRef)
        .then(() => this.processAddonPayment())
        .then(({ data }) => {
          this.invoiceNumber = data.invoiceNumber || null;
          if (this.request) {
            this.request.status = "PROCESSED";
          }
          responses.showSuccess("Payment completed successfully");
          return this.$router.replace({ name: "addon-activation", query: { requestId: this.requestId } });
        })
        .catch((err) => errorHandler.tomcatError(err))
        .finally(() => {
          this.paymentProcessing = false;
          this.loadingText = "Loading request details...";
        });
    },
    initPage() {
      const clientRef = this.$route.query.ClientRef;
      const requestId = clientRef || this.$route.query.requestId;
      if (!requestId) {
        this.errorMessage = "Missing requestId. Please use the payment link provided.";
        this.loading = false;
        return Promise.resolve();
      }
      this.requestId = requestId;
      this.loading = true;
      this.loadingText = "Loading request details...";
      return this.fetchRequestDetails()
        .then(() => {
          if (clientRef && this.$route.query.KazangRef) {
            return this.finalizeKazangPayment();
          }
          return null;
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },

  beforeMount() {
    this.initPage();
  }
};
</script>

<style scoped>
.addon-activation-view {
  min-height: 100vh;
  background: var(--bg-page, #fafafa);
  padding: var(--space-6, 1.5rem) var(--space-4, 1rem);
}

.container {
  max-width: 800px;
  margin: 0 auto;
}

/* Section Header */
.section-header {
  margin-bottom: var(--space-6, 1.5rem);
}

.section-title {
  font-size: var(--text-4xl, 1.5rem);
  font-weight: var(--font-semibold, 600);
  color: var(--text-primary, #18181b);
  margin: 0;
}

/* Payment Layout */
.payment-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-6, 1.5rem);
}

/* Price Breakdown */
.price-breakdown {
  display: flex;
  flex-direction: column;
  gap: var(--space-2, 0.5rem);
}

.price-row {
  display: flex;
  justify-content: space-between;
  font-size: var(--text-md, 0.875rem);
  color: var(--text-secondary, #52525b);
}

.price-row--total {
  padding-top: var(--space-3, 0.75rem);
  border-top: 1px solid var(--border-color, #e4e4e7);
  font-weight: var(--font-semibold, 600);
  color: var(--text-primary, #18181b);
}

.total-amount {
  color: var(--color-primary-500, #0ea5e9);
  font-size: var(--text-xl, 1rem);
}

/* Method Selection */
.method-selection {
  display: flex;
  flex-direction: column;
  gap: var(--space-3, 0.75rem);
}

.selection-prompt {
  font-size: var(--text-md, 0.875rem);
  color: var(--text-muted, #71717a);
  margin: 0 0 var(--space-2, 0.5rem) 0;
}

.method-btn {
  display: flex;
  align-items: center;
  gap: var(--space-4, 1rem);
  width: 100%;
  padding: var(--space-4, 1rem);
  background: var(--bg-card, #ffffff);
  border: 1px solid var(--border-color, #e4e4e7);
  border-radius: var(--radius-lg, 0.5rem);
  cursor: pointer;
  transition: all var(--transition-base, 0.2s);
  text-align: left;
}

.method-btn:hover:not(:disabled) {
  border-color: var(--color-primary-500, #0ea5e9);
  background: var(--color-primary-50, #f0f9ff);
}

.method-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.method-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-lg, 0.5rem);
  background: var(--color-primary-50, #f0f9ff);
  color: var(--color-primary-500, #0ea5e9);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.method-icon svg {
  width: 20px;
  height: 20px;
}

.method-icon--kazang {
  background: var(--color-warning-100, #fef3c7);
  color: var(--color-warning-600, #d97706);
}

.method-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.method-name {
  font-size: var(--text-lg, 0.9375rem);
  font-weight: var(--font-semibold, 600);
  color: var(--text-primary, #18181b);
}

.method-desc {
  font-size: var(--text-base, 0.8125rem);
  color: var(--text-muted, #71717a);
}

.method-arrow {
  width: 20px;
  height: 20px;
  color: var(--color-gray-400, #a1a1aa);
}

/* Selected Method */
.selected-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4, 1rem);
  padding-bottom: var(--space-4, 1rem);
  border-bottom: 1px solid var(--border-color-light, #f4f4f5);
}

.selected-label {
  font-size: var(--text-md, 0.875rem);
  font-weight: var(--font-semibold, 600);
  color: var(--color-primary-500, #0ea5e9);
}

/* Kazang Section */
.kazang-section,
.eft-section {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.payment-prompt {
  font-size: var(--text-md, 0.875rem);
  color: var(--text-muted, #71717a);
  margin: 0 0 var(--space-4, 1rem) 0;
}

/* Success Notice */
.success-notice {
  display: flex;
  align-items: center;
  gap: var(--space-3, 0.75rem);
  margin-top: var(--space-4, 1rem);
  padding: var(--space-3, 0.75rem) var(--space-4, 1rem);
  background: var(--color-success-50, #ecfdf5);
  border: 1px solid var(--color-success-200, #a7f3d0);
  border-radius: var(--radius-lg, 0.5rem);
  color: var(--color-success-800, #065f46);
  font-size: var(--text-base, 0.8125rem);
}

.success-notice svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* Invoice Badge */
.invoice-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2, 0.5rem);
  background: var(--bg-muted, #f4f4f5);
  padding: var(--space-3, 0.75rem) var(--space-4, 1rem);
  border-radius: var(--radius-lg, 0.5rem);
  margin-top: var(--space-4, 1rem);
}

.badge-label {
  font-size: var(--text-sm, 0.75rem);
  color: var(--text-muted, #71717a);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge-value {
  font-size: var(--text-lg, 0.9375rem);
  font-weight: var(--font-semibold, 600);
  color: var(--text-primary, #18181b);
}

/* Utility */
.mb-4 {
  margin-bottom: var(--space-4, 1rem);
}

/* Responsive */
@media (max-width: 768px) {
  .payment-layout {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: var(--text-3xl, 1.25rem);
  }
}

@media (max-width: 576px) {
  .addon-activation-view {
    padding: var(--space-4, 1rem) var(--space-3, 0.75rem);
  }
}
</style>
